import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { MobileNav } from "@/components/mobile-nav";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CreditCard, Bell, User, LogOut, Moon } from "lucide-react";

// SettingsTabContent components (이 import들은 사용되고 있다고 가정)
import { AccountSettingsTab } from "@/components/settings-tabs/account-settings-tab";
import { SubscriptionSettingsTab } from "@/components/settings-tabs/subscription-settings-tab";
import { NotificationSettingsTab } from "@/components/settings-tabs/notification-settings-tab";
import { AppearanceSettingsTab } from "@/components/settings-tabs/appearance-settings-tab";

export function SettingsPage() {
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // 로그아웃 로딩 상태
  const [activeTab, setActiveTab] = useState("account");

  const subscriptionQuery = useQuery({
    queryKey: ["subscription", session?.user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stripe_user_subscriptions")
        .select("*")
        .eq("user_id", session!.user.id)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!session?.user,
    staleTime: 1000 * 60 * 5,
  });

  // 최적화된 로그아웃 핸들러
  const handleSignOut = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      // 로컬 변수에 성공 메시지 저장 (토스트용)
      const successMessage = {
        title: "로그아웃 성공",
        description: "안전하게 로그아웃되었습니다.",
      };
      
      // 로그아웃 실행
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // 성공 메시지 표시
      toast(successMessage);
      
      // 로컬 스토리지 정리 작업은 제외
      // 히스토리 스택 정리를 위해 replace:true 사용
      navigate("/", { replace: true });
    } catch (error: any) {
      toast({
        title: "로그아웃 실패",
        description: error.message || "로그아웃 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  // 로그인 안 된 상태 UI
  if (!session) {
    return (
      <div className="flex min-h-screen flex-col">
        <header className="container border-b py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">설정</h1>
            <ThemeToggle />
          </div>
        </header>
        <main className="container flex-1 flex items-center justify-center">
          {" "}
          {/* 중앙 정렬 추가 */}
          <Card className="w-full max-w-sm">
            {" "}
            {/* 최대 너비 설정 */}
            <CardHeader className="text-center">
              {" "}
              {/* 텍스트 중앙 정렬 */}
              <CardTitle>로그인 필요</CardTitle>
              <CardDescription>
                설정 페이지를 보려면 로그인이 필요합니다.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => navigate("/auth")} className="w-full">
                로그인 페이지로 이동
              </Button>
            </CardFooter>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  // 로그인 된 상태 UI
  return (
    <div className="flex min-h-screen flex-col">
      <header className="container border-b py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">설정</h1>
          <ThemeToggle />
        </div>
      </header>

      <main className="container flex-1 space-y-6 py-8 pb-24 md:pb-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden border">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-accent/5">
                <CardTitle>설정</CardTitle>
                <CardDescription>계정 및 앱 설정을 관리하세요</CardDescription>
              </CardHeader>

              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="flex w-full p-0 h-auto bg-transparent">
                  <TabsTrigger
                    value="account"
                    className="flex-1 py-3 rounded-none border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent"
                  >
                    <div className="flex flex-col items-center sm:flex-row sm:gap-2">
                      <User className="h-4 w-4" />
                      <span>계정</span>
                    </div>
                  </TabsTrigger>

                  <TabsTrigger
                    value="subscription"
                    className="flex-1 py-3 rounded-none border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent"
                  >
                    <div className="flex flex-col items-center sm:flex-row sm:gap-2">
                      <CreditCard className="h-4 w-4" />
                      <span>구독</span>
                    </div>
                  </TabsTrigger>

                  <TabsTrigger
                    value="notifications"
                    className="flex-1 py-3 rounded-none border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent"
                  >
                    <div className="flex flex-col items-center sm:flex-row sm:gap-2">
                      <Bell className="h-4 w-4" />
                      <span>알림</span>
                    </div>
                  </TabsTrigger>

                  <TabsTrigger
                    value="appearance"
                    className="flex-1 py-3 rounded-none border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent"
                  >
                    <div className="flex flex-col items-center sm:flex-row sm:gap-2">
                      <Moon className="h-4 w-4" />
                      <span>테마</span>
                    </div>
                  </TabsTrigger>
                </TabsList>

                <div className="p-6">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <TabsContent value="account" className="m-0">
                      <AccountSettingsTab session={session} />
                    </TabsContent>

                    <TabsContent value="subscription" className="m-0">
                      {subscriptionQuery.isLoading ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <SubscriptionSettingsTab
                          subscription={subscriptionQuery.data}
                        />
                      )}
                    </TabsContent>

                    <TabsContent value="notifications" className="m-0">
                      <NotificationSettingsTab />
                    </TabsContent>

                    <TabsContent value="appearance" className="m-0">
                      <AppearanceSettingsTab />
                    </TabsContent>
                  </motion.div>
                </div>
              </Tabs>

              <CardFooter className="border-t p-6">
                <Button
                  variant="outline"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={handleSignOut}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      {" "}
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 로그아웃
                      중...{" "}
                    </>
                  ) : (
                    <>
                      {" "}
                      <LogOut className="mr-2 h-4 w-4" /> 로그아웃{" "}
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}